import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { TrendingUp, LogOut, Shield, UserPlus, Lock, Check, X } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

import AdminNavbar from '../components/AdminNavbar';

const AdminManagementPage = () => {
    const { logout } = useAuthStore();
    const [subAdmins, setSubAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        permissions: []
    });

    const availablePermissions = [
        { id: 'dashboard.view', label: 'View Dashboard' },
        { id: 'users.view', label: 'View Users' },
        { id: 'users.edit', label: 'Edit Users' },
        { id: 'users.block', label: 'Block Users' },
        { id: 'kyc.view', label: 'View KYC' },
        { id: 'kyc.edit', label: 'Review KYC' },
        { id: 'tokens.view', label: 'View Tokens' },
        { id: 'tokens.edit', label: 'Manage Tokens' },
        { id: 'markets.view', label: 'View Markets' },
        { id: 'markets.edit', label: 'Manage Markets' },
        { id: 'withdrawals.view', label: 'View Withdrawals' },
        { id: 'withdrawals.edit', label: 'Approve Withdrawals' },
    ];

    useEffect(() => {
        fetchSubAdmins();
    }, []);

    const fetchSubAdmins = async () => {
        try {
            const res = await api.get('/admin/sub-admins');
            setSubAdmins(res.data.subAdmins);
        } catch (error) {
            toast.error('Failed to fetch sub-admins');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSubAdmin = async (e) => {
        e.preventDefault();
        try {
            await api.post('/admin/sub-admins', formData);
            toast.success('Sub-admin created successfully');
            setShowCreateModal(false);
            setFormData({
                email: '',
                password: '',
                firstName: '',
                lastName: '',
                permissions: []
            });
            fetchSubAdmins();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to create sub-admin');
        }
    };

    const togglePermission = (permissionId) => {
        setFormData(prev => {
            const permissions = prev.permissions.includes(permissionId)
                ? prev.permissions.filter(p => p !== permissionId)
                : [...prev.permissions, permissionId];
            return { ...prev, permissions };
        });
    };

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />

            <div className="container mx-auto px-6 py-8">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                        <Shield className="text-blue-500" />
                        Admin Management
                    </h1>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-bold transition-all"
                    >
                        <UserPlus size={20} />
                        Create Sub-Admin
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-20 text-gray-400">Loading...</div>
                ) : (
                    <div className="grid gap-6">
                        {subAdmins.map((admin) => (
                            <div key={admin.id} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="text-xl font-bold text-white">{admin.firstName} {admin.lastName}</h3>
                                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded uppercase">
                                                {admin.role}
                                            </span>
                                        </div>
                                        <p className="text-gray-400 mb-4">{admin.email}</p>

                                        <div className="flex flex-wrap gap-2">
                                            {admin.permissions && admin.permissions.map(perm => (
                                                <span key={perm} className="px-2 py-1 bg-slate-700 text-gray-300 text-xs rounded border border-slate-600">
                                                    {perm}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="text-right">
                                        <div className="text-sm text-gray-500 mb-2">
                                            Created: {new Date(admin.createdAt).toLocaleDateString()}
                                        </div>
                                        <button className="text-blue-400 hover:text-blue-300 text-sm font-medium">
                                            Edit Permissions
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {subAdmins.length === 0 && (
                            <div className="text-center py-12 bg-slate-800 rounded-xl border border-slate-700 border-dashed">
                                <Shield size={48} className="mx-auto text-slate-600 mb-4" />
                                <p className="text-gray-400 text-lg">No sub-admins found</p>
                                <p className="text-gray-500 text-sm">Create a sub-admin to delegate tasks</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-white">Create Sub-Admin</h2>
                            <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateSubAdmin} className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-400 mb-2">First Name</label>
                                    <input
                                        type="text"
                                        value={formData.firstName}
                                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-blue-500 focus:outline-none"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-400 mb-2">Last Name</label>
                                    <input
                                        type="text"
                                        value={formData.lastName}
                                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-blue-500 focus:outline-none"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-400 mb-2">Email</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-blue-500 focus:outline-none"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-400 mb-2">Password</label>
                                <input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-blue-500 focus:outline-none"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-400 mb-4">Permissions</label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {availablePermissions.map((perm) => (
                                        <label
                                            key={perm.id}
                                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${formData.permissions.includes(perm.id)
                                                ? 'bg-blue-600/20 border-blue-500/50'
                                                : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                                                }`}
                                        >
                                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${formData.permissions.includes(perm.id)
                                                ? 'bg-blue-500 border-blue-500'
                                                : 'border-gray-500'
                                                }`}>
                                                {formData.permissions.includes(perm.id) && <Check size={14} className="text-white" />}
                                            </div>
                                            <input
                                                type="checkbox"
                                                className="hidden"
                                                checked={formData.permissions.includes(perm.id)}
                                                onChange={() => togglePermission(perm.id)}
                                            />
                                            <span className={`text-sm font-medium ${formData.permissions.includes(perm.id) ? 'text-blue-400' : 'text-gray-400'
                                                }`}>
                                                {perm.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-all shadow-lg shadow-blue-900/20"
                            >
                                Create Sub-Admin
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminManagementPage;
